import NotchedBlock from "../NotchedBlock";
import ModalCloseIcon from "@/assets/img/layout/close-modal.svg?react";
import SaveIcon from "@/assets/img/symbolic/save.svg?react";
import PencilIcon from "@/assets/img/symbolic/pencil.svg?react";
import { AuthContext } from "@/Realize";
import { useContext, useState } from "react";
import UIListener from "@/pages/home/UIListener";
import social, { SUCCESS } from "@/api/social";
import type { WindTypes } from "@/util/Tailwind";
import { Keyboard } from "@capacitor/keyboard";
import { twMerge } from "tailwind-merge";

function EditLabel({
    label,
    className,
}: {
    label: string;
    className?: string;
})
{
    return (
        <label
            className={twMerge(Wind.style({
                display: "block",
                fontSize: "text-lg",
                fontWeight: "font-semibold",
                color: "text-[#6C3331]",
                marginBottom: "mb-2",
            }).class, className)}
        >
            {label}
        </label>
    );
}

function EditInput({
    label,
    placeholder,
    limit,
    value,
    type,
    onChange,
    onFocus,
    onBlur,

}: { label: string;
    placeholder: string;
    limit?: number;
    type?: "textarea"|"input";
    value: string;
    onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onFocus?: () => void;
    onBlur?: () => void; })
{
    const [inputValue, setValue] = useState(value);
    const charCount = value.length;
    const isOverLimit = limit !== undefined && charCount >= limit;

    return (
        <div className="mb-4 w-full">
            <div
                className={Wind.style({
                    display: "flex",
                    flexDirection: "flex-row",
                    gap: "gap-1",
                    justifyContent: "justify-between",
                    alignItems: "items-center",
                }).class}
            >
                <EditLabel
                    label={label}
                />
                {limit !== undefined && (
                    <div
                        className={Wind.style({
                            textAlign: "text-right",
                            fontSize: "text-xs",
                            color: isOverLimit ? "text-red-500" : "text-gray-400",
                            marginTop: "mt-1",
                        }).class}
                    >
                        {charCount}
                        /
                        {limit}
                    </div>
                )}
            </div>
            {type === "textarea" ? (
                <textarea
                    placeholder={placeholder}
                    value={inputValue}
                    onChange={e =>
                    {
                        setValue(e.target.value);

                        if (onChange)
                        {
                            onChange(e);
                        }
                    }}
                    maxLength={limit ? limit : undefined}
                    className={Wind.style({
                        width: "w-full",
                        padding: "p-2",
                        backgroundColor: "bg-transparent",
                        borderRadius: "rounded-md",
                        borderColor: isOverLimit ? "border-red-500" : "border-gray-300",
                        borderWidth: "border",
                        resize: "resize-none",
                        ":focus": {
                            outlineWidth: "focus:outline-[0]",
                            borderColor: isOverLimit ? "focus:border-sharp-red" : "focus:border-[#6C3331]",
                        }
                    }).class}
                    onFocus={onFocus}
                    onBlur={onBlur}
                />
            ) : null}
            {type === "input" && (
                <input
                    type="text"
                    placeholder={placeholder}
                    value={value}
                    onChange={e =>
                    {
                        setValue(e.target.value);

                        if (onChange)
                        {
                            onChange(e);
                        }
                    }}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    maxLength={limit ? limit : undefined}
                    className={Wind.style({
                        width: "w-full",
                        padding: "p-2",
                        backgroundColor: "bg-transparent",
                        borderColor: isOverLimit ? "border-red-500" : "border-gray-300",
                        borderBottomWidth: "border-b-2",
                        ":focus": {
                            outlineWidth: "focus:outline-[0]",
                            borderColor: isOverLimit ? "focus:border-sharp-red" : "focus:border-[#6C3331]",
                        }
                    }).class}
                />
            )}

        </div>
    );
}

const fiveMB = 5242880; // 5MB in bytes (Maximum file size for avatar upload)

export default function ProfileEditor({
    opacity,
}: {
    opacity?: WindTypes["opacity"];
})
{
    const userData = useContext(AuthContext);

    const [editorName, setEditorName] = useState(userData?.username ?? "");
    const [editorBio, setEditorBio] = useState(userData?.bio ?? "");
    const [imageData, setImageData] = useState<string|null>(userData?.photo_path ?? null);
    const [isSaving, setIsSaving] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    const triggerSave = async () =>
    {
        setIsSaving(true);

        if (!userData)
        {
            UIListener.trigger("setError", {
                title: "Profile Update Error",
                error: "You must be logged in to update your profile.",
            });

            setIsSaving(false);

            return;
        }

        if (editorName.trim() === "")
        {
            UIListener.trigger("setError", {
                title: "Profile Update Error",
                error: "Display name cannot be empty.",
            });

            setIsSaving(false);

            return;
        }

        if (editorBio.length > SUCCESS)
        {
            UIListener.trigger("setError", {
                title: "Profile Update Error",
                error: "Bio cannot exceed 200 characters.",
            });

            setIsSaving(false);

            return;
        }

        if (editorName === userData.username && editorBio === userData.bio && imageData === userData.photo_path)
        {
            UIListener.trigger("setError", {
                title: "Profile Update Error",
                error: "No changes detected. Please modify your profile before saving.",
            });

            setIsSaving(false);

            return;
        }

        Logger.log("Saving profile changes:", {
            username: editorName,
            bio: editorBio,
        });

        // Check if the image data has been changed from the initial user data
        if (imageData !== null && imageData !== userData.photo_path)
        {
            // Upload Image
            await social.uploadAvatar(imageData).then((response) =>
            {
                if (response?.status === SUCCESS)
                {
                    if (response.data)
                    {
                        Logger.log("Avatar uploaded successfully:", response.data);
                        localStorage.setItem("access_token", response.data.token);

                        setTimeout(() =>
                        {
                            UIListener.trigger("validateToken", {});
                        }, 100);
                    }
                }
                else
                {
                    Logger.error("Failed to upload avatar:", response);

                    UIListener.trigger("setError", {
                        title: "Avatar Upload Error",
                        error: "Failed to upload avatar. Please try again later.",
                    });
                }
            })
                .catch((error) =>
                {
                    Logger.error("Error uploading avatar:", error);

                    UIListener.trigger("setError", {
                        title: "Avatar Upload Error",
                        error: "Failed to upload avatar. Please try again later.",
                    });
                });
        }

        if (editorName !== userData.username || editorBio !== userData.bio)
        {
            social.saveDetails(editorName, editorBio).then((response) =>
            {
                if (response?.status === SUCCESS)
                {
                    if (response.data.token)
                    {
                        localStorage.setItem("access_token", response.data.token);
                        UIListener.trigger("validateToken", {});

                        setIsSaving(false);
                        UIListener.trigger("openProfileEditMode", {});
                    }
                }
            })
                .catch((error) =>
                {
                    Logger.error("Error saving profile details:", error);

                    UIListener.trigger("setError", {
                        title: "Profile Update Error",
                        error: "Failed to save profile details. Please try again later.",
                    });

                    setIsSaving(false);
                });
        }
        else
        {
            setIsSaving(false);
            UIListener.trigger("openProfileEditMode", {});
        }
    };

    return (
        <div
            className={Wind.style({
                position: "relative",
                width: "w-full",
                paddingX: "px-4",
                opacity: opacity ?? "opacity-100",
                transition: "transition-opacity ease-in-out",
                transitionDuration: "duration-300",
            }).class}
        >
            <input
                type="file"
                accept="image/*"
                className="hidden"
                id="avatar-upload"
                onChange={(e) =>
                {
                    const file = e.target.files?.[0];

                    if (file)
                    {
                    // Handle file upload logic here
                        if (file.size > fiveMB) // 5MB limit
                        {
                            UIListener.trigger("setError", {
                                title: "Avatar Upload Error",
                                error: "File size exceeds 5MB limit.",
                            });

                            return;
                        }

                        if (!file.type.startsWith("image/"))
                        {
                            UIListener.trigger("setError", {
                                title: "Avatar Upload Error",
                                error: "Invalid file type. Please upload an image.",
                            });

                            return;
                        }

                        Logger.log("Uploading avatar file:", file);

                        // Convert to base64 image
                        const reader = new FileReader();

                        reader.onloadend = () =>
                        {
                            if (reader.result)
                            {
                                const photoData = reader.result as string;

                                setImageData(photoData);
                            }
                        };

                        reader.readAsDataURL(file);
                    }
                }}
            />
            <NotchedBlock
                className={Wind.style({
                    width: "w-full",
                    paddingTop: "pt-10",
                    paddingX: "px-4",
                    paddingBottom: "pb-6",
                    transition: "transition-transform ease-in-out",
                    transitionDuration: "duration-300",
                    transformTranslateY: isEditing
                        ? "-translate-y-[calc(var(--safe-area-keyboard)_/_2)]"
                        : "translate-y-0"
                }).class}
            >
                <h1
                    className={Wind.style({
                        fontSize: "text-4xl",
                        fontFamily: "font-poppins",
                        fontWeight: "font-bold",
                        lineHeight: "leading-9",
                        textAlign: "text-center",
                        color: "text-[#6C3331]",
                        marginBottom: "mb-6",
                    }).class}
                >
                    Edit Profile
                </h1>
                <div
                    className={Wind.style({
                        display: "flex",
                        flexDirection: "flex-col",
                        alignItems: "items-center",
                    }).class}
                >
                    <div
                        className={Wind.style({
                            display: "inline-block",
                            marginX: "mx-auto",
                            position: "relative",
                            marginBottom: "mb-4",
                        }).class}
                        onClick={() =>
                        {
                            Logger.log("Open avatar upload dialog");

                            const fileInput = document.getElementById("avatar-upload") as HTMLInputElement|null;

                            if (fileInput)
                            {
                                fileInput.click();
                            }
                        }}
                    >
                        <img
                            src={imageData ?? ""}
                            alt={userData?.username ?? "User Avatar"}
                            className={Wind.style({
                                width: "w-[4.5rem]",
                                height: "h-[4.5rem]",
                                borderRadius: "rounded-full",
                                objectFit: "object-cover",
                            }).class}
                        />
                        <PencilIcon
                            className={Wind.style({
                                width: "w-7",
                                height: "h-7",
                                padding: "p-1",
                                position: "absolute",
                                bottom: "bottom-0",
                                right: "right-0",
                                color: "text-white",
                                backgroundColor: "bg-[#3F383A]",
                                borderRadius: "rounded-full",
                                borderWidth: "border-2",
                                borderColor: "border-parchment"
                            }).class}
                        />
                    </div>
                    <EditInput
                        label="Display Name"
                        placeholder="Enter your name to be displayed to others"
                        limit={35}
                        value={editorName}
                        onChange={e => void setEditorName(e.target.value)}
                        type="input"
                        onFocus={() =>
                        {
                            setIsEditing(true);
                        }}
                        onBlur={() =>
                        {
                            setIsEditing(false);

                            Keyboard.hide().catch((error) =>
                            {
                                Logger.error("Error hiding keyboard:", error);
                            });
                        }}
                    />
                    {/* <div
                        className={Wind.style({
                            textAlign: "text-left",
                            width: "w-full",
                        }).class}
                    >
                        <EditLabel
                            label="Social Handles"
                        />
                    </div> */}
                    <EditInput
                        label="Bio"
                        placeholder="Say something about yourself"
                        limit={200}
                        value={editorBio}
                        onChange={e => void setEditorBio(e.target.value)}
                        type="textarea"
                        onFocus={() =>
                        {
                            setIsEditing(true);
                        }}
                        onBlur={() =>
                        {
                            setIsEditing(false);

                            Keyboard.hide().catch((error) =>
                            {
                                Logger.error("Error hiding keyboard:", error);
                            });
                        }}
                    />
                    <button
                        className={Wind.style({
                            width: "w-full",
                            height: "h-12",
                            backgroundColor: "bg-sharp-red",
                            color: "text-white",
                            fontSize: "text-base",
                            lineHeight: "leading-6",
                            fontFamily: "font-poppins",
                            fontWeight: "font-semibold",
                            borderRadius: "rounded-full",
                            marginTop: "mt-4",
                            position: "relative",
                            display: "flex",
                            justifyContent: "justify-center",
                            alignItems: "items-center",
                            ":disabled": {
                                backgroundColor: "disabled:bg-gray-400",
                            }
                        }).class}
                        onClick={() => void triggerSave()}
                        disabled={isSaving}
                    >
                        <SaveIcon
                            className={Wind.style({
                                width: "w-9",
                                height: "h-9",
                                padding: "p-1",
                                position: "absolute",
                                top: "top-1/2",
                                left: "left-4",
                                transformTranslateY: "-translate-y-1/2",
                            }).class}
                        />
                        {isSaving ? "Saving..." : "Save Changes"}
                    </button>
                </div>
            </NotchedBlock>
            <button
                className={Wind.style({
                    position: "absolute",
                    top: "-top-2",
                    right: "right-6",
                    transformTranslateY: isEditing ? "-translate-y-[--safe-area-keyboard]" : "translate-y-0"
                }).class}
                disabled={isSaving}
                onClick={() =>
                {
                    UIListener.trigger("openProfileEditMode", {});
                }}
            >
                <ModalCloseIcon className="w-8 h-8" />
            </button>
        </div>
    );
}
